import {useEffect} from "react";
import {router, useNavigationContainerRef} from "expo-router";
import {consumePendingRoute} from "@/lib/pending-route";

/** How long to keep waiting for the root navigator, and how often to look. */
const READY_POLL_INTERVAL_MS = 50;
const READY_POLL_ATTEMPTS = 20;

/**
 * Renders nothing. Replays the destination that arrived before the app could go there.
 *
 * Mounted in `app/(app)/_layout.tsx`, INSIDE `<Authenticated>`: existing at all is this
 * component's way of saying the user is past the gate. That is the whole design — the tap handler
 * above the gate never has to ask when login will finish, it just leaves the href behind and this
 * mounts when it mounts.
 *
 * Placed after the navigator in that layout so the `<Stack>` has committed first.
 */
export function PendingRouteHandler() {
    const navigationRef = useNavigationContainerRef();

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let attemptsLeft = READY_POLL_ATTEMPTS;

        const attempt = () => {
            timer = null;

            // Not decoration. `router.push` throws before the root navigator has mounted, and
            // React commits a child's effects before its parent's — so a component this deep can
            // in principle run while the container above it is still finishing. In practice the
            // auth gate resolves asynchronously and this mounts long after, which is why the wait
            // is bounded and short: it is insurance, not a mechanism.
            if (!navigationRef.isReady()) {
                if (attemptsLeft > 0) {
                    attemptsLeft -= 1;
                    timer = setTimeout(attempt, READY_POLL_INTERVAL_MS);
                }
                return;
            }

            // Read and cleared in one step by the store itself, which is what makes this happen
            // EXACTLY once: whatever runs this effect a second time — a remount when the auth
            // gate flips, a Fast Refresh — finds nothing left, so a deep link cannot fire twice
            // and a login an hour later cannot resurrect it.
            const href = consumePendingRoute();
            if (href) {
                router.push(href);
            }
        };

        attempt();

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [navigationRef]);

    return null;
}
